import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { Store } from "@ngrx/store";
import { Md2Dialog } from "md2";
import { Subscription } from "rxjs/Subscription";
import { Language } from "../../models/language";
import { ReferentialEntity } from "../../models/referential-entity";
import { User } from "../../models/user";
import { Value } from "../../models/value";
import { FiltersService } from "../../services/filters.service";
import { ReferentialService } from "../../services/referential.service";
import { SearchByTermPipe } from "../../pipes/search-by-term.pipe";

@Component({
  selector: "app-list-selector",
  templateUrl: "./list-selector.component.html",
  styleUrls: ["./list-selector.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ListSelectorComponent implements OnInit, OnDestroy {
  loaded: boolean;
  loading: boolean;
  allValues: ReferentialEntity[];
  unselectedValues: ReferentialEntity[];
  selectedValues: ReferentialEntity[];
  subscriptions: Subscription[];
  language: Language;
  term: string;
  updated: number;
  included = true;
  hiddenValue = true;
  checkedValue = false;
  Dommyfields: string[] = ["name", "id"];
  unselectedValuesPiped: ReferentialEntity[];
  selectedValuesPiped: ReferentialEntity[];

  @Input() values: ReferentialEntity[];
  @Input() selected: ReferentialEntity[];
  @Input() type: Value;
  @Input() multiple: boolean;
  @Input() forceInter: boolean;
  @Input() includeMode = false;
  @Input() nonSport = false;

  @Output("selectorUpdated") selectorUpdated = new EventEmitter();

  @ViewChild("selection") selectionModal: Md2Dialog;
  /**
   * Initialize variables
   * @param {ReferentialService} referentialService
   * @param {Store<any>} store
   * @param {FiltersService} filtersService
   */
  constructor(
    private referentialService: ReferentialService,
    private store: Store<any>,
    private filtersService: FiltersService,
    private searchByTermPipe: SearchByTermPipe
  ) {
    this.loaded = false;
    this.loading = false;
    this.selectedValues = [];
    this.subscriptions = [];
    this.updated = Date.now();
  }

  /**
   * Set selected values from Input and get current language
   */
  ngOnInit() {
    this.subscriptions.push(
      this.store.select("userState").subscribe((user: User) => {
        if (user && user.currentLanguage) {
          this.language = user.currentLanguage;
          this.processValues();
        }
      })
    );

    if (
      this.selectedValues &&
      this.selectedValues.length > 0 &&
      this.selectedValues[0]
    ) {
      this.included = this.selectedValues[0].isIncluded;
    }
  }

  AddAll() {
    this.unselectedValuesPiped = this.searchByTermPipe.transform(
      this.unselectedValues,
      this.term,
      this.Dommyfields
    );

    for (var i = 0; i < this.unselectedValuesPiped.length; i++) {
      this.selectValue(this.unselectedValuesPiped[i]);
    }
  }

  RemoveAll() {
    this.selectedValuesPiped = this.searchByTermPipe.transform(
      this.selectedValues,
      this.term,
      this.Dommyfields
    );

    for (var i = 0; i < this.selectedValuesPiped.length; i++) {
      this.unselectValue(this.selectedValuesPiped[i]);
    }
  }

  /**
   * Process values from input and handle the language change
   */
  processValues() {
    this.unselectedValues = this.values;
    if (this.selectedValues) {
      this.selectedValues = this.selected;
      if (this.selectedValues.length > 0) {
        this.loadValues();
      }
    }
  }

  /**
   * Unsubscribe on destroy and close the modal
   */
  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.selectionModal.close();
  }

  /**
   * Process the value selection according to the selector type (multiple or single selection)
   * Add the entity to the filtersService (to filter the others selectors)
   * Emit the update event
   * @param {ReferentialEntity} value
   */
  selectValue(value: ReferentialEntity) {
    value.isIncluded = this.included;
    value.referentialId = this.type.id;
    this.unselectedValues = this.unselectedValues.filter(
      (item) => item.id !== value.id
    );
    if (this.multiple) {
      this.selectedValues = this.selectedValues.concat([value]);
    } else {
      this.selectedValues = [value];
      this.selectionModal.close();
    }

    this.filtersService.addEntity(
      new ReferentialEntity({
        referentialId: this.type.id,
        id: value.id,
        name: value.name,
        value: value.name,
      })
    );
    this.updated = Date.now();
    this.selectorUpdated.emit();
  }

  /**
   * Remove the value from the selected values
   * Remove the entity from the filters service
   * Emit the update event
   * @param {Value} value
   */
  unselectValue(value: ReferentialEntity) {
    this.selectedValues = this.selectedValues.filter(
      (item) => item.id !== value.id
    );
    this.unselectedValues = this.unselectedValues.concat([value]);
    this.filtersService.removeEntity(
      new ReferentialEntity({
        referentialId: this.type.id,
        id: value.id,
        name: value.name,
        value: value.name,
      })
    );

    this.updated = Date.now();
    this.selectorUpdated.emit();

    this.checkedValue = false;
    this.hiddenValue = true;
  }

  /**
   * Open the modal and load the selectable values if they are not loaded yet
   */
  openSelector() {
    this.selectionModal.open();
    if (!this.loaded) {
      this.loadValues();
    }
  }

  /**
   * Load the selectable values from the API according to the type, language and already selected values in others selectors)
   */
  loadValues() {
    this.loading = true;
    let languageId = this.language.id;
    if (this.forceInter) {
      languageId = 0;
    }

    this.referentialService
      .searchReferential(
        this.type.value,
        languageId,
        this.nonSport,
        this.filtersService.selectedReferentialEntities
      )
      .subscribe((res) => {
        this.allValues = res;
        this.unselectedValues = res.filter((value) => {
          return (
            this.selectedValues.filter((selected) => selected.id === value.id)
              .length === 0
          );
        });
        this.translateValues();
        this.loading = false;
      });
  }

  /**
   * Remove all the selected values
   */
  clearSelection() {
    this.selectedValues.forEach((value) => this.unselectValue(value));
  }

  /**
   * Translate values according to the current language
   */
  translateValues() {
    this.selectedValues.map((selectedValue) => {
      const valueFound = this.allValues.find(
        (value) => value.id === selectedValue.id
      );
      if (valueFound) {
        selectedValue.name = valueFound.name;
      }
    });
  }

  close() {
    if (
      this.includeMode &&
      this.selectedValues &&
      this.selectedValues.length > 0 &&
      this.selectedValues[0]
    ) {
      this.selectValue(this.selectedValues[0]);
    } else {
      this.selectionModal.close();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/components/list-selector/list-selector.component.ts